import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { MapContainer, Marker, Polyline, Circle, useMap } from 'react-leaflet';
import { MapboxTileLayer, makeCircleIcon } from '../../lib/leafletInit';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { db, auth, ixoraoneAuth } from '../../firebase';
import { useAdmin } from '../../contexts/AdminContext';
import { useZones } from '../../hooks/useZones';
import { useSupervisorLocationHistory } from '../../hooks/useSupervisorLocationHistory';
import { locationSamplesToMapPoints } from '../../lib/supervisorLocationApi';

const SEND_SMS_URL = 'https://asia-south1-ixora-feedback-86789.cloudfunctions.net/sendSmsToSupervisor';
const SET_ALLOTTED_SITES_URL = 'https://asia-south1-ixora-feedback-86789.cloudfunctions.net/setSupervisorAllottedSites';

/** Returns the best available Firebase ID token for the current session. */
async function getAdminToken() {
  try {
    const tok = await ixoraoneAuth.currentUser?.getIdToken();
    if (tok) return tok;
  } catch { /* fall through */ }
  try {
    const tok = await auth.currentUser?.getIdToken();
    if (tok) return tok;
  } catch { /* fall through */ }
  return null;
}

// ---------- constants ----------

const INDIA_CENTER = [20.5937, 78.9629];
const MAX_RANGE_DAYS = 31;
const MAX_RANGE_SAMPLES = 5000;

// ---------- helpers ----------

const pad2 = (n) => String(n).padStart(2, '0');

const toLocalDateInputValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseDateInputValue = (value) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const formatTime = (date) =>
  `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDurationHrs = (ms) => {
  if (!ms || ms < 0) return '0.0h';
  return `${(ms / 3_600_000).toFixed(1)}h`;
};

// Haversine distance in meters.
const haversineMeters = (a, b) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

const totalDistanceKm = (points) => {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1], points[i]);
  }
  return total / 1000;
};

// Speed between two points in km/h
const speedKmh = (a, b) => {
  const distM = haversineMeters(a, b);
  const dtSec = Math.max(1, Math.abs(b.t - a.t) / 1000);
  return (distM / dtSec) * 3.6;
};

// Live-pin recency colour: green < 5min, amber < 30min, red > 30min
const recencyColor = (ts) => {
  if (!ts) return '#DC2626';
  const nowMs = Date.now();
  const thenMs = ts.toMillis ? ts.toMillis() : Number(ts);
  const diffMin = (nowMs - thenMs) / 60_000;
  if (diffMin < 5) return '#16A34A';
  if (diffMin < 30) return '#D97706';
  return '#DC2626';
};

const trackingChip = (status) => {
  const map = {
    active: { label: 'ACTIVE', color: 'var(--c-success)', bg: 'rgba(22,163,74,.10)' },
    paused: { label: 'PAUSED', color: 'var(--c-warning)', bg: 'rgba(217,119,6,.10)' },
    denied: { label: 'DENIED', color: 'var(--c-danger)', bg: 'rgba(220,38,38,.10)' },
  };
  const cfg =
    map[status] || { label: 'UNKNOWN', color: 'var(--c-ink-3)', bg: 'rgba(107,114,128,.10)' };
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.12em',
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        padding: '3px 8px',
        display: 'inline-block',
      }}
    >
      {cfg.label}
    </span>
  );
};

// ---------- map helpers ----------

/** Fit map to an array of {lat,lng} points. */
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 16); return; }
    map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40] });
  }, [map, points]);
  return null;
};

// ---------- BatterySparkline ----------

const BatterySparkline = ({ samples }) => {
  if (!samples || samples.length === 0) {
    return (
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '.12em',
          color: 'var(--c-ink-3)',
          padding: 'var(--sp-4) 0',
        }}
      >
        NO BATTERY DATA
      </div>
    );
  }
  const w = 280;
  const h = 60;
  const padX = 4;
  const padY = 6;
  const t0 = samples[0].t;
  const tN = samples[samples.length - 1].t;
  const span = Math.max(1, tN - t0);
  const points = samples.map((s) => {
    const x = padX + ((s.t - t0) / span) * (w - padX * 2);
    const y = padY + (1 - Math.max(0, Math.min(100, s.b)) / 100) * (h - padY * 2);
    return [x, y];
  });
  const d = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', maxWidth: '100%' }}>
      <line x1={padX} y1={padY} x2={w - padX} y2={padY} stroke="var(--c-line)" strokeWidth="1" />
      <line
        x1={padX}
        y1={h - padY}
        x2={w - padX}
        y2={h - padY}
        stroke="var(--c-line)"
        strokeWidth="1"
      />
      <path d={d} fill="none" stroke="#EA580C" strokeWidth="2" />
    </svg>
  );
};

// ---------- AnomalyLog ----------

const AnomalyLog = ({ points }) => {
  const anomalies = useMemo(() => {
    const out = [];
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (p.isMocked) {
        out.push({ t: p.t, type: 'MOCKED', date: p.date });
      } else if (i > 0) {
        const spd = speedKmh(points[i - 1], p);
        if (spd > 100) {
          out.push({ t: p.t, type: `SPEED ${Math.round(spd)} km/h`, date: p.date });
        }
      }
    }
    return out.slice(0, 50); // cap at 50 entries
  }, [points]);

  if (anomalies.length === 0) {
    return (
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '.10em',
          color: 'var(--c-ink-3)',
          padding: 'var(--sp-3) 0',
        }}
      >
        NO ANOMALIES DETECTED
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {anomalies.map((a, i) => (
        <div
          key={i}
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.10em',
            color: a.type === 'MOCKED' ? 'var(--c-danger)' : 'var(--c-warning)',
            display: 'flex',
            gap: 8,
          }}
        >
          <span style={{ fontVariantNumeric: 'tabular-nums slashed-zero' }}>
            {a.date ? formatTime(a.date) : '—'}
          </span>
          <span>|</span>
          <span>{a.type}</span>
        </div>
      ))}
    </div>
  );
};

// ---------- VisitTimeline ----------

const VisitTimeline = ({ visits, loading }) => {
  if (loading) {
    return (
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
        LOADING…
      </div>
    );
  }
  if (!visits || visits.length === 0) {
    return (
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
        NO SITE VISITS RECORDED FOR THIS PERIOD
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {visits.map((v) => {
        const enteredDate = v.enteredAt?.toDate ? v.enteredAt.toDate() : null;
        const leftDate = v.leftAt?.toDate ? v.leftAt.toDate() : null;
        const dwell = v.dwellMs || 0;
        const isOpen = !leftDate;
        const isDriveby = dwell > 0 && dwell < 120_000; // < 2 min
        const isGood = leftDate && dwell >= 300_000; // >= 5 min and closed
        const dotColor = isOpen ? 'var(--c-ink-3)' : isDriveby ? 'var(--c-warning)' : isGood ? 'var(--c-success)' : 'var(--c-ink-3)';
        return (
          <div
            key={v.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '8px 1fr',
              gap: 8,
              alignItems: 'start',
              paddingBottom: 8,
              borderBottom: '1px solid var(--c-line-soft)',
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--c-ink)', letterSpacing: '.08em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={v.siteName || v.siteId || undefined}
              >
                {v.siteName || v.siteId || '—'}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-3)', letterSpacing: '.08em', fontVariantNumeric: 'tabular-nums slashed-zero', marginTop: 2 }}>
                {enteredDate ? formatTime(enteredDate) : '—'}
                {' → '}
                {leftDate ? formatTime(leftDate) : 'OPEN'}
                {dwell > 0 ? ` · ${formatDuration(dwell)}` : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- shared UI primitives ----------

const SectionCard = ({ children, style }) => (
  <Box
    sx={{
      border: '1px solid var(--c-line-soft)',
      background: 'var(--c-bg)',
      padding: 'var(--sp-4)',
      ...style,
    }}
  >
    {children}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      letterSpacing: '.16em',
      fontWeight: 600,
      color: 'var(--c-ink-3)',
      textTransform: 'uppercase',
      marginBottom: 'var(--sp-2)',
    }}
  >
    {children}
  </Typography>
);

const StatCell = ({ label, value }) => (
  <Box
    sx={{
      border: '1px solid var(--c-line-soft)',
      background: 'var(--c-bg)',
      padding: 'var(--sp-3)',
    }}
  >
    <Typography
      sx={{
        fontFamily: 'var(--font-mono)',
        fontSize: 9,
        letterSpacing: '.16em',
        color: 'var(--c-ink-3)',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'var(--font-mono)',
        fontSize: 18,
        fontVariantNumeric: 'tabular-nums slashed-zero',
        color: 'var(--c-ink)',
        marginTop: '4px',
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ---------- Tab bar ----------

const TAB_VIEWS = ['today', 'date', 'range'];
const TAB_LABELS = ['Today', 'By date', 'Range'];

const TabBar = ({ activeView, onChange }) => (
  <div
    style={{
      display: 'flex',
      borderBottom: '1px solid var(--c-line-soft)',
      marginBottom: 'var(--sp-4)',
    }}
  >
    {TAB_LABELS.map((label, i) => {
      const view = TAB_VIEWS[i];
      const active = activeView === view;
      return (
        <button
          key={label}
          type="button"
          onClick={() => onChange(view)}
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            padding: 'var(--sp-2) var(--sp-4)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            borderBottom: active ? '2px solid #EA580C' : '2px solid transparent',
            color: active ? '#EA580C' : 'var(--c-ink-3)',
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

// ---------- map panel ----------

const MapPanel = ({ points, livePosition, liveColor, showFeedbackPins, feedbackPins, historyLoading }) => {
  const initialCenter = points[0] ? [points[0].lat, points[0].lng]
    : livePosition ? [livePosition.lat, livePosition.lng]
    : INDIA_CENTER;
  const initialZoom = (points[0] || livePosition) ? 14 : 5;

  const sitePoints = points.filter((p) => p.siteId && p.siteId.length > 0);
  const navigate = useNavigate();

  const allFitPoints = livePosition ? [...points, livePosition] : points;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRight: { md: '1px solid var(--c-line-soft)' },
        background: 'var(--c-bg-2)',
        minHeight: 360,
        height: '100%',
      }}
    >
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl
      >
        <MapboxTileLayer />
        <FitBounds points={allFitPoints} />
        {points.length >= 2 && (
          <Polyline
            positions={points.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: '#EA580C', opacity: 0.9, weight: 3 }}
          />
        )}
        {/* Start pin */}
        {points.length > 0 && (
          <Marker position={[points[0].lat, points[0].lng]} icon={makeCircleIcon('#16a34a', 16)} />
        )}
        {/* End pin */}
        {points.length > 1 && (
          <Marker position={[points[points.length - 1].lat, points[points.length - 1].lng]} icon={makeCircleIcon('#dc2626', 16)} />
        )}
        {/* Live position dot */}
        {livePosition && (
          <Marker
            position={[livePosition.lat, livePosition.lng]}
            icon={makeCircleIcon(liveColor, 16)}
          />
        )}
        {/* Site geofence circles (18 m radius, matching original) */}
        {sitePoints.map((p) => (
          <Circle
            key={`site-${p.id}`}
            center={[p.lat, p.lng]}
            radius={18}
            pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.35, weight: 2, opacity: 0.8 }}
          />
        ))}
        {/* Feedback pins */}
        {showFeedbackPins && feedbackPins.map((p) => (
          <Marker
            key={`fb-${p.id}`}
            position={[p.lat, p.lng]}
            icon={makeCircleIcon('#16a34a', 12)}
            eventHandlers={{ click: () => navigate(`/feedback/admin/${p.id}`) }}
            title={p.shopName}
          />
        ))}
      </MapContainer>

      {historyLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1000,
            background: 'var(--c-bg)',
            border: '1px solid var(--c-line-soft)',
            padding: '6px 10px',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.12em',
            color: 'var(--c-ink-3)',
          }}
        >
          LOADING…
        </Box>
      )}
      {!historyLoading && points.length === 0 && !livePosition && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1000,
            background: 'var(--c-bg)',
            border: '1px solid var(--c-line-soft)',
            padding: '6px 10px',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.12em',
            color: 'var(--c-ink-3)',
          }}
        >
          NO LOCATION SAMPLES FOR THIS DATE
        </Box>
      )}
    </Box>
  );
};

// ---------- right rail — Today / By-date shared ----------

const DayRail = ({ points, visits, visitsLoading, historyError, historyLoading, batterySamples }) => {
  const stats = useMemo(() => {
    if (points.length === 0) return { distanceKm: 0, durationMs: 0, samples: 0, mockedCount: 0 };
    const first = points[0];
    const last = points[points.length - 1];
    const mockedCount = points.reduce((acc, p) => acc + (p.isMocked ? 1 : 0), 0);
    return {
      distanceKm: totalDistanceKm(points),
      durationMs: Math.max(0, last.t - first.t),
      samples: points.length,
      mockedCount,
    };
  }, [points]);

  const lastSamples = useMemo(() => points.slice(-20).reverse(), [points]);

  return (
    <Box
      sx={{
        overflowY: 'auto',
        padding: 'var(--sp-4)',
        background: 'var(--c-bg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Summary stats */}
      <Box>
        <SectionLabel>Summary</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--sp-2)' }}>
          <StatCell label="Distance" value={`${stats.distanceKm.toFixed(2)} km`} />
          <StatCell label="Tracked" value={formatDuration(stats.durationMs)} />
          <StatCell label="Samples" value={stats.samples} />
          <StatCell label="Mock Flags" value={stats.mockedCount} />
        </Box>
      </Box>

      {/* Visit timeline */}
      <SectionCard>
        <SectionLabel>Site Visits</SectionLabel>
        <VisitTimeline visits={visits} loading={visitsLoading} />
      </SectionCard>

      {/* Battery sparkline */}
      <SectionCard>
        <SectionLabel>Battery (0–100%)</SectionLabel>
        <BatterySparkline samples={batterySamples} />
        {batterySamples.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '4px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--c-ink-3)',
              fontVariantNumeric: 'tabular-nums slashed-zero',
            }}
          >
            <span>{formatTime(new Date(batterySamples[0].t))}</span>
            <span>{formatTime(new Date(batterySamples[batterySamples.length - 1].t))}</span>
          </Box>
        )}
      </SectionCard>

      {/* Anomaly log */}
      <SectionCard>
        <SectionLabel>Anomaly Log</SectionLabel>
        <AnomalyLog points={points} />
      </SectionCard>

      {/* Last samples list */}
      <SectionCard style={{ flex: '1 1 auto', minHeight: 240, padding: 0 }}>
        <Box
          sx={{
            padding: 'var(--sp-3) var(--sp-4)',
            borderBottom: '1px solid var(--c-line-soft)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
            Last Samples
          </Typography>
          <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)', fontVariantNumeric: 'tabular-nums slashed-zero' }}>
            {Math.min(20, points.length)} / {points.length}
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {historyError ? (
            <Box sx={{ padding: 'var(--sp-4)' }}>
              <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-danger)' }}>
                ERROR: {historyError}
              </Typography>
            </Box>
          ) : lastSamples.length === 0 ? (
            <Box sx={{ padding: 'var(--sp-4)' }}>
              <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
                {historyLoading ? 'LOADING…' : 'NO SAMPLES'}
              </Typography>
            </Box>
          ) : (
            lastSamples.map((p) => (
              <Box
                key={p.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '76px 1fr auto',
                  gap: 'var(--sp-2)',
                  alignItems: 'center',
                  padding: '8px var(--sp-4)',
                  borderBottom: '1px solid var(--c-line-soft)',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink-2)' }}>
                  {p.date ? formatTime(p.date) : '—'}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)' }}>
                  {p.battery != null && p.battery !== '' ? `${Math.round(Number(p.battery) * 100)}%` : '—'}
                  {p.isCharging ? ' ⚡' : ''}
                </span>
                {p.isMocked ? (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.14em', fontWeight: 700, color: 'var(--c-danger)', background: 'var(--c-danger-soft)', padding: '2px 6px' }}>
                    MOCK
                  </span>
                ) : (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.14em', color: 'var(--c-ink-3)' }}>
                    OK
                  </span>
                )}
              </Box>
            ))
          )}
        </Box>
      </SectionCard>
    </Box>
  );
};

// ---------- Range rail ----------

const RangeRail = ({ points, rangeLoading, rangeTruncated }) => {
  const stats = useMemo(() => {
    if (points.length === 0) return { distanceKm: 0, durationMs: 0, samples: 0, mockedCount: 0, uniqueSites: 0 };
    const first = points[0];
    const last = points[points.length - 1];
    const mockedCount = points.reduce((acc, p) => acc + (p.isMocked ? 1 : 0), 0);
    const uniqueSites = new Set(points.map((p) => p.siteId).filter(Boolean)).size;
    return {
      distanceKm: totalDistanceKm(points),
      durationMs: Math.max(0, last.t - first.t),
      samples: points.length,
      mockedCount,
      uniqueSites,
    };
  }, [points]);

  // Top sites by visit count
  const topSites = useMemo(() => {
    const counts = {};
    for (const p of points) {
      if (p.siteId) counts[p.siteId] = (counts[p.siteId] || 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [points]);

  const maxCount = topSites.length > 0 ? topSites[0][1] : 1;

  if (rangeLoading) {
    return (
      <Box sx={{ overflowY: 'auto', padding: 'var(--sp-4)', background: 'var(--c-bg)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
          LOADING…
        </div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflowY: 'auto',
        padding: 'var(--sp-4)',
        background: 'var(--c-bg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {rangeTruncated && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.10em',
            color: 'var(--c-warning)',
            background: 'rgba(217,119,6,.08)',
            border: '1px solid rgba(217,119,6,.30)',
            padding: '6px 10px',
          }}
        >
          SHOWING FIRST {MAX_RANGE_SAMPLES} SAMPLES — NARROW THE RANGE FOR FULL DETAIL
        </div>
      )}

      <Box>
        <SectionLabel>Summary</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--sp-2)' }}>
          <StatCell label="Distance" value={`${stats.distanceKm.toFixed(2)} km`} />
          <StatCell label="Duration" value={formatDurationHrs(stats.durationMs)} />
          <StatCell label="Samples" value={stats.samples} />
          <StatCell label="Mock Flags" value={stats.mockedCount} />
          <StatCell label="Unique Sites" value={stats.uniqueSites} />
        </Box>
      </Box>

      {topSites.length > 0 && (
        <SectionCard>
          <SectionLabel>Top Sites by Sample Count</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {topSites.map(([siteId, count]) => (
              <div key={siteId} style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.08em', color: 'var(--c-ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                    {siteId}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.08em', fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink-3)' }}>
                    {count}
                  </span>
                </div>
                <div style={{ height: 6, background: 'var(--c-line-soft)', position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${(count / maxCount) * 100}%`,
                      background: '#EA580C',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {points.length === 0 && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
          NO SAMPLES IN THIS RANGE
        </div>
      )}
    </Box>
  );
};

// ---------- hook: fetch visits for a date range ----------

const useVisits = (uid, startDate, endDate) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uid || !startDate || !endDate) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'visits'),
          where('supervisorUid', '==', uid),
          where('enteredAt', '>=', Timestamp.fromDate(startDate)),
          where('enteredAt', '<', Timestamp.fromDate(endDate)),
          orderBy('enteredAt', 'asc'),
          limit(100),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        // visits collection may not exist yet — fail silently
        console.warn('useVisits query failed (collection may not exist yet):', err.message);
        if (!cancelled) setVisits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, startDate, endDate]);

  return { visits, loading };
};

// ---------- helpers for date boundaries ----------

const dayStart = (dateStr) => parseDateInputValue(dateStr);
const dayEnd = (dateStr) => {
  const d = parseDateInputValue(dateStr);
  d.setDate(d.getDate() + 1);
  return d;
};

// ---------- main ----------

const SupervisorDetail = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAdmin();

  // SMS modal
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsMsg, setSmsMsg] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null); // { ok, text }

  const handleSendSms = useCallback(async () => {
    if (!smsMsg.trim() || smsSending) return;
    setSmsSending(true);
    setSmsResult(null);
    try {
      const token = await getAdminToken();
      if (!token) {
        setSmsResult({ ok: false, text: 'Not signed in as admin.' });
        return;
      }
      const res = await fetch(SEND_SMS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ supervisorId: uid, message: smsMsg.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setSmsResult({ ok: true, text: 'SMS sent.' });
        setSmsMsg('');
        setTimeout(() => setSmsOpen(false), 1200);
      } else {
        setSmsResult({ ok: false, text: data.error === 'supervisor_has_no_valid_phone'
          ? 'This supervisor has no phone number on file.'
          : `Send failed: ${data.error || 'unknown error'}` });
      }
    } catch (err) {
      setSmsResult({ ok: false, text: `Network error: ${err.message}` });
    } finally {
      setSmsSending(false);
    }
  }, [uid, smsMsg, smsSending]);

  // Phone-edit modal
  const [phoneEditOpen, setPhoneEditOpen] = useState(false);
  const [phoneEditValue, setPhoneEditValue] = useState('');
  const [phoneEditSaving, setPhoneEditSaving] = useState(false);
  const [phoneEditResult, setPhoneEditResult] = useState(null); // { ok, text }

  const openPhoneEdit = useCallback(() => {
    setPhoneEditValue(supervisor?.siteManagerPhone || supervisor?.adminPhone || '');
    setPhoneEditResult(null);
    setPhoneEditOpen(true);
  }, [supervisor]);

  const handleSavePhone = useCallback(async () => {
    if (phoneEditSaving) return;
    const raw = phoneEditValue.trim().replace(/\D/g, '');
    // Allow empty (clears the field) or a 10-digit Indian mobile number.
    if (raw && raw.length !== 10) {
      setPhoneEditResult({ ok: false, text: 'Enter a 10-digit mobile number, or leave blank to clear.' });
      return;
    }
    setPhoneEditSaving(true);
    setPhoneEditResult(null);
    try {
      const ref = doc(db, 'supervisors', uid);
      if (raw) {
        await updateDoc(ref, { siteManagerPhone: raw });
        setSupervisor((prev) => prev ? { ...prev, siteManagerPhone: raw } : prev);
        setPhoneEditResult({ ok: true, text: `Saved — supervisor will call ${raw}.` });
      } else {
        await updateDoc(ref, { siteManagerPhone: deleteField() });
        setSupervisor((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          delete next.siteManagerPhone;
          return next;
        });
        setPhoneEditResult({ ok: true, text: 'Cleared — will fall back to "No admin phone configured".' });
      }
      setTimeout(() => setPhoneEditOpen(false), 1400);
    } catch (err) {
      setPhoneEditResult({ ok: false, text: `Save failed: ${err.message}` });
    } finally {
      setPhoneEditSaving(false);
    }
  }, [uid, phoneEditValue, phoneEditSaving]);

  // Zone allotment modal
  const { zones } = useZones();
  const [zoneOpen, setZoneOpen] = useState(false);
  const [zonePickedId, setZonePickedId] = useState('');
  const [zoneSaving, setZoneSaving] = useState(false);
  const [zoneResult, setZoneResult] = useState(null); // { ok, text }
  // Resolved manager + phone for the currently picked zone (preview)
  const [zoneManagerPreview, setZoneManagerPreview] = useState(null); // { name, phone } | null
  const zonePreviewAbortRef = useRef(null);

  const openZoneModal = useCallback(() => {
    // Pre-select the supervisor's current zone if any
    const currentSiteId = supervisor?.allotted_site_ids?.[0] || '';
    setZonePickedId(currentSiteId);
    setZoneResult(null);
    setZoneManagerPreview(null);
    setZoneOpen(true);
  }, [supervisor]);

  // When zone selection changes, resolve the site manager + phone (live preview)
  useEffect(() => {
    if (!zoneOpen || !zonePickedId) {
      setZoneManagerPreview(null);
      return;
    }
    // Cancel any previous in-flight query
    if (zonePreviewAbortRef.current) zonePreviewAbortRef.current = true;
    const abortFlag = { cancelled: false };
    zonePreviewAbortRef.current = abortFlag;

    (async () => {
      try {
        const smSnap = await getDocs(
          query(
            collection(db, 'supervisors'),
            where('userRole', '==', 'siteManager'),
          )
        );
        if (abortFlag.cancelled) return;
        for (const smDoc of smSnap.docs) {
          const d = smDoc.data();
          const smSites = d.allotted_site_ids || [];
          if (smSites.includes(zonePickedId)) {
            const phone = (d.phone_number || '').trim();
            setZoneManagerPreview({ name: d.display_name || smDoc.id, phone: phone || null });
            return;
          }
        }
        setZoneManagerPreview({ name: null, phone: null }); // zone has no manager yet
      } catch { /* ignore */ }
    })();
    return () => { abortFlag.cancelled = true; };
  }, [zonePickedId, zoneOpen]);

  const handleSaveZone = useCallback(async () => {
    if (zoneSaving || !zonePickedId) return;
    setZoneSaving(true);
    setZoneResult(null);
    try {
      const token = await getAdminToken();
      if (!token) { setZoneResult({ ok: false, text: 'Not signed in as admin.' }); return; }
      const res = await fetch(SET_ALLOTTED_SITES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ supervisorId: uid, siteIds: [zonePickedId] }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        // Optimistically update local supervisor state so the phone chip reflects immediately
        setSupervisor((prev) => prev ? {
          ...prev,
          allotted_site_ids: [zonePickedId],
          ...(zoneManagerPreview?.phone ? { siteManagerPhone: zoneManagerPreview.phone } : {}),
        } : prev);
        setZoneResult({ ok: true, text: `Zone saved.${data.siteManagerPhoneSet ? ' Admin phone auto-assigned.' : ''}` });
        setTimeout(() => setZoneOpen(false), 1200);
      } else {
        setZoneResult({ ok: false, text: `Failed: ${data.error || 'unknown error'}` });
      }
    } catch (err) {
      setZoneResult({ ok: false, text: `Network error: ${err.message}` });
    } finally {
      setZoneSaving(false);
    }
  }, [uid, zonePickedId, zoneSaving, zoneManagerPreview]);

  // Tab state
  const activeView = TAB_VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'today';

  // Shared date states
  const today = toLocalDateInputValue(new Date());
  const [byDate, setByDate] = useState(() => searchParams.get('from') || today);
  const [rangeFrom, setRangeFrom] = useState(() => {
    const f = searchParams.get('from');
    if (activeView === 'range' && f) return f;
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return toLocalDateInputValue(d);
  });
  const [rangeTo, setRangeTo] = useState(() => {
    const t = searchParams.get('to');
    if (activeView === 'range' && t) return t;
    return today;
  });
  const [rangeError, setRangeError] = useState(null);

  // Feedback pins state
  const [showFeedbackPins, setShowFeedbackPins] = useState(false);
  const [feedbackPins, setFeedbackPins] = useState([]);

  // Supervisor doc
  const [supervisor, setSupervisor] = useState(null);
  const [supervisorLoading, setSupervisorLoading] = useState(true);

  const setTab = useCallback((view) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync byDate / rangeFrom / rangeTo to URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeView === 'date') {
      next.set('from', byDate);
      next.delete('to');
    } else if (activeView === 'range') {
      next.set('from', rangeFrom);
      next.set('to', rangeTo);
    } else {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next, { replace: true });
  }, [activeView, byDate, rangeFrom, rangeTo]);

  // Validate range
  useEffect(() => {
    if (activeView !== 'range') { setRangeError(null); return; }
    const f = parseDateInputValue(rangeFrom);
    const t = parseDateInputValue(rangeTo);
    const diffDays = (t - f) / 86_400_000;
    if (diffDays < 0) { setRangeError('End date must be after start date'); return; }
    if (diffDays > MAX_RANGE_DAYS) { setRangeError(`Range exceeds ${MAX_RANGE_DAYS} days — narrow the selection`); return; }
    setRangeError(null);
  }, [activeView, rangeFrom, rangeTo]);

  // Supervisor doc fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSupervisorLoading(true);
      try {
        const snap = await getDoc(doc(db, 'supervisors', uid));
        if (cancelled) return;
        setSupervisor(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (err) {
        console.error('SupervisorDetail getDoc error:', err);
        if (!cancelled) setSupervisor(null);
      } finally {
        if (!cancelled) setSupervisorLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // --- Data for Today tab ---
  const todayStart = useMemo(() => dayStart(today), [today]);
  const todayEnd = useMemo(() => dayEnd(today), [today]);
  const {
    history: todayHistory,
    liveLocation,
    liveLocationAt,
    loading: todayLoading,
    error: todayError,
  } = useSupervisorLocationHistory(
    activeView === 'today' ? uid : null,
    todayStart,
    todayEnd,
    500,
    { includeLive: activeView === 'today' },
  );
  const todayPoints = useMemo(() => locationSamplesToMapPoints(todayHistory), [todayHistory]);
  const { visits: todayVisits, loading: todayVisitsLoading } = useVisits(
    activeView === 'today' ? uid : null, todayStart, todayEnd,
  );
  const todayBatterySamples = useMemo(
    () => todayPoints.filter((p) => p.battery != null && p.battery !== '').map((p) => ({ t: p.t, b: Number(p.battery) })),
    [todayPoints],
  );

  // --- Data for By-date tab ---
  const byDateStart = useMemo(() => dayStart(byDate), [byDate]);
  const byDateEnd = useMemo(() => dayEnd(byDate), [byDate]);
  const { history: dateHistory, loading: dateLoading, error: dateError } = useSupervisorLocationHistory(
    activeView === 'date' ? uid : null, byDateStart, byDateEnd, 500,
  );
  const datePoints = useMemo(() => locationSamplesToMapPoints(dateHistory), [dateHistory]);
  const { visits: dateVisits, loading: dateVisitsLoading } = useVisits(
    activeView === 'date' ? uid : null, byDateStart, byDateEnd,
  );
  const dateBatterySamples = useMemo(
    () => datePoints.filter((p) => p.battery != null && p.battery !== '').map((p) => ({ t: p.t, b: Number(p.battery) })),
    [datePoints],
  );

  // --- Data for Range tab ---
  const rangeStart = useMemo(() => dayStart(rangeFrom), [rangeFrom]);
  const rangeEnd = useMemo(() => {
    const d = parseDateInputValue(rangeTo);
    d.setDate(d.getDate() + 1);
    return d;
  }, [rangeTo]);
  const { history: rangeHistory, loading: rangeLoading } = useSupervisorLocationHistory(
    (activeView === 'range' && !rangeError) ? uid : null, rangeStart, rangeEnd, MAX_RANGE_SAMPLES + 1,
  );
  const rangePoints = useMemo(() => locationSamplesToMapPoints(rangeHistory), [rangeHistory]);
  const rangeTruncated = rangePoints.length > MAX_RANGE_SAMPLES;
  const rangePointsCapped = useMemo(() => rangePoints.slice(0, MAX_RANGE_SAMPLES), [rangePoints]);

  // --- Feedback pins (shared) ---
  const activeDateStr = activeView === 'today' ? today : activeView === 'date' ? byDate : null;
  useEffect(() => {
    if (!showFeedbackPins || !activeDateStr) { setFeedbackPins([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const start = parseDateInputValue(activeDateStr);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        const q = query(
          collection(db, 'feedback_collection'),
          where('feedback_collector_firebase_userID', '==', uid),
          where('feedback_collection_DateTime', '>=', Timestamp.fromDate(start)),
          where('feedback_collection_DateTime', '<', Timestamp.fromDate(end)),
          orderBy('feedback_collection_DateTime', 'asc'),
          limit(100),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const pins = snap.docs
          .map((d) => {
            const data = d.data();
            // Don't pin silent_duplicate fraud-trap submissions on the map.
            if (data.silent_duplicate === true) return null;
            const geo = data.geoAtSubmit ?? data.feedback_collection_location;
            const lat = geo?.lat ?? geo?.latitude;
            const lng = geo?.lng ?? geo?.longitude;
            if (lat == null || lng == null) return null;
            return { id: d.id, lat, lng, shopName: data.shop_name || '—' };
          })
          .filter(Boolean);
        setFeedbackPins(pins);
      } catch (err) {
        console.error('SupervisorDetail feedbackPins fetch error:', err);
        if (!cancelled) setFeedbackPins([]);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, activeDateStr, showFeedbackPins]);

  // Decide live-pin colour based on recency
  const liveColor = recencyColor(liveLocationAt);

  // Derive current tab's map points
  const activePoints = activeView === 'today' ? todayPoints : activeView === 'date' ? datePoints : rangePointsCapped;
  const activeHistoryLoading = activeView === 'today' ? todayLoading : activeView === 'date' ? dateLoading : rangeLoading;

  return (
    <div className="db-page" style={{ minHeight: '100vh' }}>
      {/* pulse animation for live dot */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { box-shadow: 0 0 0 2px ${liveColor}44; }
          50% { box-shadow: 0 0 0 8px ${liveColor}22; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes pulse-dot { from, to { box-shadow: none; } }
        }
      `}</style>

      {/* Header bar */}
      <div
        className="db-ribbon"
        style={{
          borderBottom: '1px solid var(--c-line-soft)',
          padding: 'var(--sp-3) var(--pad-h)',
          minHeight: 64,
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', minWidth: 0 }}>
          <IconButton
            size="small"
            onClick={() => navigate('/supervisors')}
            aria-label="back"
            sx={{
              color: 'var(--c-ink-2)',
              borderRadius: 0,
              border: '1px solid var(--c-line-soft)',
              '&:hover': { background: 'var(--c-bg-2)', color: 'var(--c-ink)' },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "'Geist', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--c-ink)',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {supervisorLoading ? 'LOADING…' : supervisor?.display_name || 'UNKNOWN SUPERVISOR'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '.12em',
                color: 'var(--c-ink-3)',
                marginTop: '2px',
              }}
            >
              {supervisor?.ixora_user_id || '—'}
            </Typography>
          </Box>
          <Box sx={{ marginLeft: 'var(--sp-2)' }}>
            {trackingChip(supervisor?.tracking || '')}
          </Box>
          {isAdmin && !supervisorLoading && (
            <>
              <button
                type="button"
                onClick={openZoneModal}
                title="Assign zone to supervisor"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  fontWeight: 600,
                  color: supervisor?.allotted_site_ids?.length ? 'var(--c-ink-2)' : 'var(--c-ink-4)',
                  background: 'transparent',
                  border: '1px solid var(--c-line-2)',
                  padding: '4px 10px',
                  cursor: 'pointer',
                  marginLeft: 'var(--sp-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span style={{ fontSize: 11 }}>🗺</span>
                {(() => {
                  const siteId = supervisor?.allotted_site_ids?.[0];
                  const zone = siteId ? zones.find((z) => z.id === siteId) : null;
                  return zone ? zone.name : <span style={{ color: 'var(--c-ink-4)' }}>ASSIGN ZONE</span>;
                })()}
              </button>
              <button
                type="button"
                onClick={openPhoneEdit}
                title="Set admin phone number for this supervisor"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  fontWeight: 600,
                  color: supervisor?.siteManagerPhone ? 'var(--c-ink-2)' : 'var(--c-ink-3)',
                  background: 'transparent',
                  border: '1px solid var(--c-line-2)',
                  padding: '4px 10px',
                  cursor: 'pointer',
                  marginLeft: 'var(--sp-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span style={{ fontSize: 11 }}>📞</span>
                {supervisor?.siteManagerPhone
                  ? <span style={{ fontVariantNumeric: 'tabular-nums slashed-zero' }}>{supervisor.siteManagerPhone}</span>
                  : <span style={{ color: 'var(--c-ink-4)' }}>SET PHONE</span>}
              </button>
              <button
                type="button"
                onClick={() => { setSmsOpen(true); setSmsResult(null); }}
                title="Send SMS to supervisor"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '.12em',
                  fontWeight: 600,
                  color: 'var(--c-brand)',
                  background: 'transparent',
                  border: '1px solid var(--c-brand)',
                  padding: '4px 10px',
                  cursor: 'pointer',
                  marginLeft: 'var(--sp-2)',
                }}
              >
                SMS
              </button>
            </>
          )}
        </Box>

        {/* Header controls — vary by tab */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap', minWidth: 0 }}>
          {(activeView === 'today' || activeView === 'date') && (
            <button
              type="button"
              onClick={() => setShowFeedbackPins((v) => !v)}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '.12em',
                fontWeight: showFeedbackPins ? 700 : 400,
                color: showFeedbackPins ? '#16a34a' : 'var(--c-ink-3)',
                background: showFeedbackPins ? 'rgba(22,163,74,.08)' : 'transparent',
                border: showFeedbackPins ? '1px solid #16a34a' : '1px solid var(--c-line-soft)',
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              {showFeedbackPins ? '📍 FEEDBACK ON' : '📍 SHOW FEEDBACK PINS'}
            </button>
          )}

          {activeView === 'date' && (
            <>
              <label
                htmlFor="supervisor-date"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}
              >
                Date
              </label>
              <input
                id="supervisor-date"
                type="date"
                value={byDate}
                max={today}
                onChange={(e) => setByDate(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', background: 'var(--c-bg)', border: '1px solid var(--c-line-2)', padding: '6px 8px', borderRadius: 0 }}
              />
            </>
          )}

          {activeView === 'range' && (
            <>
              <label style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
                From
              </label>
              <input
                type="date"
                value={rangeFrom}
                max={today}
                onChange={(e) => setRangeFrom(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', background: 'var(--c-bg)', border: '1px solid var(--c-line-2)', padding: '6px 8px', borderRadius: 0 }}
              />
              <label style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
                To
              </label>
              <input
                type="date"
                value={rangeTo}
                max={today}
                onChange={(e) => setRangeTo(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', background: 'var(--c-bg)', border: '1px solid var(--c-line-2)', padding: '6px 8px', borderRadius: 0 }}
              />
            </>
          )}
        </Box>
      </div>

      {/* Tab bar + content */}
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
        {/* Tab navigation */}
        <div style={{ padding: '0 var(--pad-h)', background: 'var(--c-bg)', flexShrink: 0 }}>
          <TabBar activeView={activeView} onChange={setTab} />
          {rangeError && activeView === 'range' && (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '.10em',
                color: 'var(--c-danger)',
                background: 'rgba(220,38,38,.06)',
                border: '1px solid rgba(220,38,38,.30)',
                padding: '6px 10px',
                marginBottom: 'var(--sp-2)',
              }}
            >
              {rangeError}
            </div>
          )}
        </div>

        {/* Body grid: map left 2/3, rail right 1/3 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* MAP — shown for today + date; range tab shows map with all range points */}
          <MapPanel
            points={activePoints}
            livePosition={activeView === 'today' ? liveLocation : null}
            liveColor={liveColor}
            showFeedbackPins={showFeedbackPins && activeView !== 'range'}
            feedbackPins={feedbackPins}
            historyLoading={activeHistoryLoading}
          />

          {/* RIGHT RAIL */}
          {activeView === 'today' && (
            <DayRail
              points={todayPoints}
              visits={todayVisits}
              visitsLoading={todayVisitsLoading}
              historyError={todayError}
              historyLoading={todayLoading}
              batterySamples={todayBatterySamples}
            />
          )}
          {activeView === 'date' && (
            <DayRail
              points={datePoints}
              visits={dateVisits}
              visitsLoading={dateVisitsLoading}
              historyError={dateError}
              historyLoading={dateLoading}
              batterySamples={dateBatterySamples}
            />
          )}
          {activeView === 'range' && (
            <RangeRail
              points={rangePointsCapped}
              rangeLoading={rangeLoading}
              rangeTruncated={rangeTruncated}
            />
          )}
        </Box>
      </div>

      {/* ── Zone allotment modal ──────────────────────────────────────── */}
      {zoneOpen && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(26,22,20,.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setZoneOpen(false); }}
        >
          <div
            style={{
              background: 'var(--c-bg)',
              border: '1px solid var(--c-line)',
              padding: '24px 24px 20px',
              width: 'min(420px, 92vw)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', fontWeight: 700, color: 'var(--c-ink-2)', textTransform: 'uppercase', flexShrink: 0 }}>
                Assign Zone
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                title={supervisor?.display_name || uid}
              >
                {supervisor?.display_name || uid}
              </span>
            </div>
            <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 12, color: 'var(--c-ink-3)', margin: 0, lineHeight: 1.5 }}>
              Assigns the supervisor to a zone. Their "Call admin" phone is resolved automatically from whoever manages that zone.
            </p>
            <select
              autoFocus
              value={zonePickedId}
              onChange={(e) => { setZoneResult(null); setZonePickedId(e.target.value); }}
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: 14,
                color: 'var(--c-ink)',
                background: 'var(--c-bg-2)',
                border: '1px solid var(--c-line)',
                padding: '10px 12px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <option value="">— pick a zone —</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>

            {/* Live preview of the manager + phone for the picked zone */}
            {zonePickedId && (
              <div style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: 12,
                background: 'var(--c-bg-2)',
                border: '1px solid var(--c-line-soft)',
                padding: '10px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>ZONE MANAGER</span>
                {zoneManagerPreview === null ? (
                  <span style={{ color: 'var(--c-ink-3)' }}>resolving…</span>
                ) : zoneManagerPreview.name ? (
                  <>
                    <span style={{ fontWeight: 600, color: 'var(--c-ink)' }}>{zoneManagerPreview.name}</span>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      fontVariantNumeric: 'tabular-nums slashed-zero',
                      color: zoneManagerPreview.phone ? 'var(--c-brand)' : 'var(--c-ink-3)',
                    }}>
                      {zoneManagerPreview.phone || '⚠ no phone on file'}
                    </span>
                    {zoneManagerPreview.phone && (
                      <span style={{ fontSize: 11, color: 'var(--c-ink-3)' }}>
                        This number will be auto-assigned as the admin phone.
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--c-warning, #D97706)' }}>No site manager assigned to this zone yet.</span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setZoneOpen(false)}
                style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                  fontWeight: 600, color: 'var(--c-ink-3)',
                  background: 'transparent', border: '1px solid var(--c-line-soft)',
                  padding: '6px 14px', cursor: 'pointer',
                }}
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={handleSaveZone}
                disabled={zoneSaving || !zonePickedId}
                style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                  fontWeight: 700, color: '#fff',
                  background: (zoneSaving || !zonePickedId) ? 'var(--c-ink-4)' : 'var(--c-brand)',
                  border: 'none', padding: '6px 14px',
                  cursor: (zoneSaving || !zonePickedId) ? 'not-allowed' : 'pointer',
                }}
              >
                {zoneSaving ? 'SAVING…' : 'SAVE'}
              </button>
            </div>
            {zoneResult && (
              <div style={{
                fontFamily: "'Geist', sans-serif", fontSize: 13,
                color: zoneResult.ok ? '#16a34a' : '#dc2626',
                background: zoneResult.ok ? 'rgba(22,163,74,.08)' : 'rgba(220,38,38,.08)',
                border: `1px solid ${zoneResult.ok ? '#16a34a' : '#dc2626'}`,
                padding: '8px 12px',
              }}>
                {zoneResult.text}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phone-edit modal ──────────────────────────────────────────── */}
      {phoneEditOpen && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(26,22,20,.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setPhoneEditOpen(false); }}
        >
          <div
            style={{
              background: 'var(--c-bg)',
              border: '1px solid var(--c-line)',
              padding: '24px 24px 20px',
              width: 'min(380px, 92vw)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', fontWeight: 700, color: 'var(--c-ink-2)', textTransform: 'uppercase', flexShrink: 0 }}>
                Admin Phone
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                title={supervisor?.display_name || uid}
              >
                {supervisor?.display_name || uid}
              </span>
            </div>
            <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 12, color: 'var(--c-ink-3)', margin: 0, lineHeight: 1.5 }}>
              The number this supervisor's app dials when they tap "Call admin". Stored as{' '}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>siteManagerPhone</span>{' '}
              on their doc. Overrides any auto-assigned value from site allocation.
            </p>
            <input
              autoFocus
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit mobile number"
              value={phoneEditValue}
              onChange={(e) => {
                setPhoneEditResult(null);
                setPhoneEditValue(e.target.value.replace(/\D/g, '').slice(0, 10));
              }}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 18,
                fontVariantNumeric: 'tabular-nums slashed-zero',
                letterSpacing: '.08em',
                color: 'var(--c-ink)',
                background: 'var(--c-bg-2)',
                border: '1px solid var(--c-line)',
                padding: '10px 14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-4)' }}>
                {phoneEditValue.length}/10{phoneEditValue.length === 10 ? ' ✓' : ''}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setPhoneEditOpen(false)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                    fontWeight: 600, color: 'var(--c-ink-3)',
                    background: 'transparent', border: '1px solid var(--c-line-soft)',
                    padding: '6px 14px', cursor: 'pointer',
                  }}
                >
                  CANCEL
                </button>
                {(supervisor?.siteManagerPhone || phoneEditValue) && (
                  <button
                    type="button"
                    onClick={() => { setPhoneEditValue(''); setPhoneEditResult(null); }}
                    disabled={phoneEditSaving || !phoneEditValue}
                    style={{
                      fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                      fontWeight: 600, color: '#dc2626',
                      background: 'transparent', border: '1px solid #dc2626',
                      padding: '6px 14px', cursor: phoneEditSaving || !phoneEditValue ? 'not-allowed' : 'pointer',
                      opacity: !phoneEditValue ? 0.4 : 1,
                    }}
                  >
                    CLEAR
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSavePhone}
                  disabled={phoneEditSaving || (phoneEditValue.length > 0 && phoneEditValue.length !== 10)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                    fontWeight: 700, color: '#fff',
                    background: (phoneEditSaving || (phoneEditValue.length > 0 && phoneEditValue.length !== 10))
                      ? 'var(--c-ink-4)'
                      : 'var(--c-brand)',
                    border: 'none', padding: '6px 14px',
                    cursor: (phoneEditSaving || (phoneEditValue.length > 0 && phoneEditValue.length !== 10)) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {phoneEditSaving ? 'SAVING…' : phoneEditValue ? 'SAVE' : 'CLEAR & SAVE'}
                </button>
              </div>
            </div>
            {phoneEditResult && (
              <div style={{
                fontFamily: "'Geist', sans-serif", fontSize: 13,
                color: phoneEditResult.ok ? '#16a34a' : '#dc2626',
                background: phoneEditResult.ok ? 'rgba(22,163,74,.08)' : 'rgba(220,38,38,.08)',
                border: `1px solid ${phoneEditResult.ok ? '#16a34a' : '#dc2626'}`,
                padding: '8px 12px',
              }}>
                {phoneEditResult.text}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SMS modal ─────────────────────────────────────────────────── */}
      {smsOpen && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(26,22,20,.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setSmsOpen(false); }}
        >
          <div
            style={{
              background: 'var(--c-bg)',
              border: '1px solid var(--c-line)',
              padding: '24px 24px 20px',
              width: 'min(420px, 92vw)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', fontWeight: 700, color: 'var(--c-ink-2)', textTransform: 'uppercase', flexShrink: 0 }}>
                Send SMS
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                title={supervisor?.display_name || uid}
              >
                {supervisor?.display_name || uid}
              </span>
            </div>
            <textarea
              autoFocus
              rows={4}
              maxLength={300}
              placeholder="Type your message…"
              value={smsMsg}
              onChange={(e) => setSmsMsg(e.target.value)}
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: 14,
                color: 'var(--c-ink)',
                background: 'var(--c-bg-2)',
                border: '1px solid var(--c-line)',
                padding: '10px 12px',
                resize: 'vertical',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-4)' }}>
                {smsMsg.length}/300 · route=q (promotional)
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setSmsOpen(false)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                    fontWeight: 600, color: 'var(--c-ink-3)',
                    background: 'transparent', border: '1px solid var(--c-line-soft)',
                    padding: '6px 14px', cursor: 'pointer',
                  }}
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={handleSendSms}
                  disabled={smsSending || !smsMsg.trim()}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                    fontWeight: 700, color: '#fff',
                    background: smsSending || !smsMsg.trim() ? 'var(--c-ink-4)' : 'var(--c-brand)',
                    border: 'none', padding: '6px 14px', cursor: smsSending || !smsMsg.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {smsSending ? 'SENDING…' : 'SEND'}
                </button>
              </div>
            </div>
            {smsResult && (
              <div style={{
                fontFamily: "'Geist', sans-serif", fontSize: 13,
                color: smsResult.ok ? '#16a34a' : '#dc2626',
                background: smsResult.ok ? 'rgba(22,163,74,.08)' : 'rgba(220,38,38,.08)',
                border: `1px solid ${smsResult.ok ? '#16a34a' : '#dc2626'}`,
                padding: '8px 12px',
              }}>
                {smsResult.text}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorDetail;
